import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Send, Download, Plus, Trash2, CheckCircle, Pencil, Mail, Eye, FlaskConical, Copy, XCircle, RotateCcw } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getAirportName } from "@/utils/airports";
import CommunicationHistory from "@/components/CommunicationHistory";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const NOTIFICATION_LABELS = {
  ticket:                { label: "E-Ticket",           color: "bg-purple-100 text-purple-800",  desc: "Standard ticket — first send" },
  schedule_change:       { label: "Schedule Change",     color: "bg-amber-100 text-amber-800",    desc: "Airline changed times/dates" },
  cancellation:          { label: "Cancellation",        color: "bg-red-100 text-red-800",        desc: "Flight cancelled by airline" },
  customer_cancellation: { label: "Booking Cancellation", color: "bg-orange-100 text-orange-800", desc: "Booking cancelled by The Better Fare / customer request" },
};

const emptySegment = () => ({
  departure_city: "", arrival_city: "",
  departure_date: "", departure_time: "",
  arrival_date:   "", arrival_time:   "",
  flight_number:  "", operated_by: "", ticket_class: "Economy",
  status: "normal",
});

/**
 * Compute connection time between two consecutive segments using the
 * local times stored on the ticket (no timezone conversion — the stored
 * dates/times are already local to each airport, which is what the
 * traveller reads off the ticket and what airline schedules show).
 *
 * Returns { minutes: number, isStopover: boolean } when both segments
 * have valid dates/times and the elapsed time is positive, or null when
 * data is missing/invalid or the connection is zero/negative.
 *
 * isStopover is true when the gap is strictly less than 24 hours
 * (the "● Stopover in …" line is shown only for those connections).
 * Gaps of 24 hours or longer are treated as separate legs and no
 * connector line is rendered at all.
 */
function computeTransit(prevSeg, nextSeg) {
  const prevArr  = prevSeg?.arrival_date  && prevSeg?.arrival_time;
  const nextDep  = nextSeg?.departure_date && nextSeg?.departure_time;
  if (!prevArr || !nextDep) return null;

  const arrMs  = Date.parse(`${prevSeg.arrival_date}T${prevSeg.arrival_time}`);
  const depMs  = Date.parse(`${nextSeg.departure_date}T${nextSeg.departure_time}`);
  if (isNaN(arrMs) || isNaN(depMs)) return null;

  const diff = depMs - arrMs;
  if (diff <= 0) return null;

  const minutes = Math.round(diff / 60000);
  return { minutes, isStopover: minutes < 24 * 60 };
}

function fmtTransitDuration(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m > 0 ? m + "m" : ""}`.trim() : `${m}m`;
}

const emptyBagSection = () => ({
  hand_quantity: 1,    hand_weight: "7kg",
  checked_quantity: 1, checked_weight: "23kg",
  personal_quantity: 0, personal_size: "40x30x15cm",
});

const DEFAULT_BAG = emptyBagSection();

export default function TicketGenerator() {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [booking,         setBooking]        = useState(null);
  const [ticket,          setTicket]         = useState(null);
  const [tickets,         setTickets]        = useState([]);
  const [selectedTicketIdx, setSelectedTicketIdx] = useState(0);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelReason,    setCancelReason]   = useState("");
  const [cancelling,      setCancelling]     = useState(false);
  const [loading,         setLoading]        = useState(true);
  const [saving,          setSaving]         = useState(false);
  const [sending,         setSending]        = useState(false);
  const [sendingTest,     setSendingTest]    = useState(false);
  const [showSendDialog,         setShowSendDialog]         = useState(false);
  const [showCancellationConfirm, setShowCancellationConfirm] = useState(false);
  const [sendingCancellation,     setSendingCancellation]     = useState(false);
  const [showPreview,     setShowPreview]    = useState(false);
  const [sendEmail,       setSendEmail]      = useState("");
  const [notifType,       setNotifType]      = useState("ticket");
  const [linkedInvoice,   setLinkedInvoice]  = useState(undefined);
  const [alsoSendInvoice, setAlsoSendInvoice] = useState(false);
  const [pnr,             setPnr]            = useState("");
  const [pnr2,            setPnr2]           = useState("");
  const [segments,        setSegments]       = useState([emptySegment()]);
  const [passengers,      setPassengers]     = useState([]);

  const defaultPax = () => ({
    ...DEFAULT_BAG,
    title: "Mr", first_name: "", last_name: "", gender: "",
    ticket_number: "", ticket_number_2: "",
    baggage_mode: "general",
    baggage_outbound: null,
    baggage_return: null,
  });

  // Normalise a loaded passenger so all fields have safe defaults
  const normPax = (p, globalBag = {}) => ({
    ...DEFAULT_BAG,
    hand_quantity:    globalBag.hand_quantity    ?? 1,
    hand_weight:      globalBag.hand_weight      ?? "7kg",
    checked_quantity: globalBag.checked_quantity ?? 1,
    checked_weight:   globalBag.checked_weight   ?? "23kg",
    ticket_number_2:  "",
    baggage_mode:     "general",
    baggage_outbound: null,
    baggage_return:   null,
    ...p,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [bRes, tRes] = await Promise.all([
          axios.get(`${API}/bookings/${bookingId}`,        { headers: getAuthHeaders() }),
          axios.get(`${API}/tickets?booking_id=${bookingId}`, { headers: getAuthHeaders() }),
        ]);
        const b = bRes.data;
        setBooking(b);
        setSendEmail(b?.passengers?.[0]?.email || "");
        const existing = tRes.data;
        setTickets(existing);
        if (existing.length > 0) {
          const t = existing[0];
          setTicket(t);
          setPnr(t.pnr || "");
          setPnr2(t.pnr2 || "");
          setSegments((t.segments || [emptySegment()]).map(s => ({ ...emptySegment(), ...s, status: s.status || "normal" })));
          setPassengers((t.passengers || []).map(p => normPax(p, t.baggage || {})));
          setNotifType(t.last_notification_type || "ticket");
        } else {
          setPnr(b.pnr || "");
          setPnr2(b.pnr2 || "");
          const pax = (b.passengers || []).map((p, i) => {
            let title = p.title || "";
            if (!title) title = ((p.gender || "").toLowerCase() === "f" || (p.gender || "").toLowerCase() === "female") ? "Ms" : "Mr";
            return normPax({
              title, first_name: p.first_name || "", last_name: p.last_name || "", gender: p.gender || "",
              ticket_number:   (b.ticket_numbers   || [])[i] || "",
              ticket_number_2: (b.ticket_numbers_2 || [])[i] || "",
            });
          });
          setPassengers(pax.length > 0 ? pax : [defaultPax()]);
          const od = b.offer_data;
          if (od?.type === "manual" && od.segments?.length > 0) {
            setSegments(od.segments.map(s => {
              const depDt = s.departure_time ? new Date(s.departure_time) : null;
              const arrDt = s.arrival_time   ? new Date(s.arrival_time)   : null;
              const fmt  = dt => dt ? dt.toTimeString().slice(0, 5) : "";
              const fmtD = dt => dt ? dt.toISOString().split("T")[0]  : "";
              return { departure_city: s.departure_airport || "", arrival_city: s.arrival_airport || "", departure_date: fmtD(depDt), departure_time: fmt(depDt), arrival_date: fmtD(arrDt), arrival_time: fmt(arrDt), flight_number: s.flight_number || "", operated_by: s.operated_by || od.airline || "", ticket_class: od.cabin_class || "Economy", status: "normal" };
            }));
          } else if (od?.type === "duffel" && od.slices?.length > 0) {
            setSegments(od.slices.flatMap(sl => sl.segments || []).map(s => ({ departure_city: s.origin_iata || "", arrival_city: s.destination_iata || "", departure_date: s.departing_at?.split("T")[0] || "", departure_time: s.departing_at?.split("T")[1]?.slice(0,5) || "", arrival_date: s.arriving_at?.split("T")[0] || "", arrival_time: s.arriving_at?.split("T")[1]?.slice(0,5) || "", flight_number: s.flight_number || "", operated_by: s.operating_carrier || "", ticket_class: s.cabin_class || "Economy", status: "normal" })));
          } else if (b.amadeus_order_id && od?.itineraries?.length > 0) {
            const fareDetails = (od.travelerPricings?.[0]?.fareDetailsBySegment || []);
            const fareBySegId = Object.fromEntries(fareDetails.map(fd => [String(fd.segmentId), fd.cabin || "Economy"]));
            const amadeusSeg = od.itineraries.flatMap(itin => (itin.segments || []).map(seg => {
              const depAt = seg.departure?.at || "";
              const arrAt = seg.arrival?.at || "";
              const carrier = seg.carrierCode || "";
              return {
                departure_city: seg.departure?.iataCode || "", arrival_city: seg.arrival?.iataCode || "",
                departure_date: depAt.split("T")[0] || "",
                departure_time: depAt.includes("T") ? depAt.split("T")[1].slice(0, 5) : "",
                arrival_date: arrAt.split("T")[0] || "",
                arrival_time: arrAt.includes("T") ? arrAt.split("T")[1].slice(0, 5) : "",
                flight_number: carrier + (seg.number || ""),
                operated_by: seg.operating?.carrierCode || carrier,
                ticket_class: fareBySegId[String(seg.id)] || "Economy",
                status: "normal",
              };
            }));
            if (amadeusSeg.length > 0) setSegments(amadeusSeg);
            if (b.pnr)  setPnr(b.pnr);
            if (b.pnr2) setPnr2(b.pnr2);
          }
        }
        axios.get(`${API}/invoices?source_type=flight_booking&source_id=${bookingId}`, { headers: getAuthHeaders() })
          .then(r => { const found = (r.data || []).find(i => i.invoice_status !== "cancelled") || null; setLinkedInvoice(found); })
          .catch(() => setLinkedInvoice(null));
      } catch { toast.error("Failed to load booking"); navigate("/bookings"); }
      finally { setLoading(false); }
    };
    load();
  }, [bookingId, navigate]);

  const updateSeg = (i, f, v) => setSegments(s => s.map((seg, idx) => idx === i ? { ...seg, [f]: v } : seg));
  const addSeg    = () => setSegments(s => [...s, emptySegment()]);
  const removeSeg = i  => setSegments(s => s.filter((_, idx) => idx !== i));
  const updatePax = (i, f, v) => setPassengers(p => p.map((pax, idx) => idx === i ? { ...pax, [f]: v } : pax));
  const addPax    = () => setPassengers(p => [...p, defaultPax()]);
  const removePax = i  => setPassengers(p => p.filter((_, idx) => idx !== i));

  // Update a nested bag field (outbound or return)
  const updateBagField = (paxIdx, bagKey, field, value) =>
    setPassengers(prev => prev.map((pax, idx) => {
      if (idx !== paxIdx) return pax;
      const current = pax[bagKey] || emptyBagSection();
      return { ...pax, [bagKey]: { ...current, [field]: value } };
    }));

  // Copy one passenger's baggage settings to all others
  const applyBaggageToAll = (srcIdx) => {
    const src = passengers[srcIdx];
    const bagFields = ["hand_quantity","hand_weight","checked_quantity","checked_weight","personal_quantity","personal_size","baggage_mode","baggage_outbound","baggage_return"];
    setPassengers(prev => prev.map((pax, idx) => {
      if (idx === srcIdx) return pax;
      const patch = {};
      bagFields.forEach(f => { patch[f] = src[f]; });
      return { ...pax, ...patch };
    }));
    toast.success("Baggage copied to all passengers");
  };

  // ─── buildHtml ─────────────────────────────────────────────────────────────
  // typeOverride lets callers (e.g. handleSendCancellation) generate a specific
  // email type without changing the notifType dropdown state.
  const buildHtml = (isTestBanner = false, typeOverride = null) => {
    const effectiveType = typeOverride || notifType;
    const customer = passengers[0] || {};

    const testBannerHtml = isTestBanner ? `
      <tr><td style="background:#fef9c3;padding:8px 40px;text-align:center;border-bottom:1px solid #fde047;">
        <p style="margin:0;font-size:12px;font-weight:700;color:#713f12;">⚠ TEST PREVIEW — Not sent to customer</p>
      </td></tr>` : "";

    const bannerHtml = effectiveType !== "ticket" ? `
      <tr><td style="background:${
        effectiveType === "schedule_change" ? "#fef3c7" :
        effectiveType === "customer_cancellation" ? "#fff7ed" : "#fee2e2"
      };padding:12px 40px;text-align:center;border-bottom:1px solid ${
        effectiveType === "schedule_change" ? "#fcd34d" :
        effectiveType === "customer_cancellation" ? "#fed7aa" : "#fca5a5"
      };">
        <p style="margin:0;font-size:13px;font-weight:700;color:${
          effectiveType === "schedule_change" ? "#92400e" :
          effectiveType === "customer_cancellation" ? "#9a3412" : "#991b1b"
        };">
          ${
            effectiveType === "schedule_change" ? "SCHEDULE CHANGE &mdash; Please review your updated flight times below" :
            effectiveType === "customer_cancellation" ? "BOOKING CANCELLATION &mdash; Your booking has been cancelled" :
            "FLIGHT CANCELLATION &mdash; Important notice about your booking"
          }
        </p>
      </td></tr>` : "";

    const segRows = segments.map((s, i) => {
      const st = s.status || "normal";
      const borderColor = st === "changed" ? "#fcd34d" : st === "cancelled" ? "#fca5a5" : "#c7d2fe";
      const headerBg    = st === "changed" ? "#fffbeb" : st === "cancelled" ? "#fff1f2" : "#f0f4ff";
      const headerColor = st === "changed" ? "#92400e" : st === "cancelled" ? "#be123c" : "#1e3a5f";
      const airportCol  = st === "changed" ? "#b45309" : st === "cancelled" ? "#be123c" : "#1e3a5f";
      const lineColor   = st === "changed" ? "#fcd34d" : st === "cancelled" ? "#fca5a5" : "#93c5fd";
      const timeStyle   = st === "cancelled" ? "text-decoration:line-through;color:#9ca3af;" : "color:#1f2937;";
      const statusBadge = st !== "normal" ? `<span style="display:inline-block;background:${st === "changed" ? "#fef3c7" : "#fee2e2"};color:${st === "changed" ? "#92400e" : "#991b1b"};font-size:10px;font-weight:700;padding:2px 8px;border-radius:20px;margin-left:8px;text-transform:uppercase;letter-spacing:0.5px;">${st === "changed" ? "Updated" : "Cancelled"}</span>` : "";
      return `
      <tr><td style="padding:2px 0;">
        <table style="width:100%;border:1px solid ${borderColor};border-radius:10px;overflow:hidden;" cellpadding="0" cellspacing="0">
          <tr><td style="background:${headerBg};padding:6px 12px;">
            <span style="font-size:10px;font-weight:700;color:${headerColor};text-transform:uppercase;letter-spacing:0.8px;">Flight ${i+1} &nbsp;&middot;&nbsp; ${s.flight_number||"&mdash;"} &nbsp;&middot;&nbsp; ${s.operated_by||"&mdash;"} &nbsp;&middot;&nbsp; ${s.ticket_class||"Economy"}</span>${statusBadge}
          </td></tr>
          <tr><td style="background:#ffffff;padding:10px 14px;border-top:1px solid ${borderColor};">
            <table style="width:100%;" cellpadding="0" cellspacing="0"><tr>
              <td style="text-align:center;width:38%;">
                <p style="margin:0;font-size:22px;font-weight:800;font-family:monospace;${timeStyle}">${s.departure_time||"--:--"}</p>
                <p style="margin:2px 0 0;font-size:10px;color:#6b7280;">${s.departure_date||""}</p>
                <p style="margin:2px 0 0;font-size:14px;font-weight:700;color:${airportCol};font-family:monospace;">${s.departure_city||"???"}</p>
                <p style="margin:1px 0 0;font-size:9px;color:#9ca3af;font-family:sans-serif;">${getAirportName(s.departure_city)||""}</p>
              </td>
              <td style="text-align:center;width:24%;padding:0 8px;">
                <div style="border-top:2px solid ${lineColor};position:relative;">
                  <span style="position:absolute;top:-10px;left:50%;transform:translateX(-50%);background:white;padding:0 4px;font-size:14px;">&#9992;</span>
                </div>
              </td>
              <td style="text-align:center;width:38%;">
                <p style="margin:0;font-size:22px;font-weight:800;font-family:monospace;${timeStyle}">${s.arrival_time||"--:--"}</p>
                <p style="margin:2px 0 0;font-size:10px;color:#6b7280;">${s.arrival_date||""}</p>
                <p style="margin:2px 0 0;font-size:14px;font-weight:700;color:${airportCol};font-family:monospace;">${s.arrival_city||"???"}</p>
                <p style="margin:1px 0 0;font-size:9px;color:#9ca3af;font-family:sans-serif;">${getAirportName(s.arrival_city)||""}</p>
              </td>
            </tr></table>
          </td></tr>
        </table>
      </td></tr>`;
    });

    // Insert transit / stopover rows between consecutive segments
    const segsHtmlWithTransit = segRows.reduce((acc, row, i) => {
      acc.push(row);
      if (i < segments.length - 1) {
        const transit = computeTransit(segments[i], segments[i + 1]);
        if (transit) {
          const city = segments[i].arrival_city || "";
          const cityName = getAirportName(city) || city;
          if (transit.isStopover) {
            const durStr = fmtTransitDuration(transit.minutes);
            acc.push(`
      <tr><td style="padding:6px 4px;">
        <table style="width:100%;border:1px dashed #fbbf24;border-radius:8px;background:#fffbeb;" cellpadding="0" cellspacing="0">
          <tr><td style="padding:8px 14px;text-align:center;">
            <span style="font-size:10px;font-weight:700;color:#92400e;text-transform:uppercase;letter-spacing:0.8px;">
              &#9679; STOPOVER in ${cityName} (${city}) &mdash; ${durStr}
            </span>
          </td></tr>
        </table>
      </td></tr>`);
          }
          // gaps >= 24 h are not stopovers — render nothing
        }
      }
      return acc;
    }, []);
    const segsHtml = segsHtmlWithTransit.join("");

    // Combined PNR reference (no separate "PNR 1 / PNR 2" labels)
    const pnrDisplay = pnr2 ? `${pnr||"PENDING"} / ${pnr2}` : (pnr || "PENDING");

    const greetingBody = notifType === "ticket"
      ? "Please find your e-ticket below. Present this document at check-in along with a valid ID."
      : notifType === "schedule_change"
        ? "Your flight schedule has been updated by the airline. Please review the highlighted segments carefully and contact us if you have any questions."
        : notifType === "customer_cancellation"
          ? "Your flight booking has been cancelled as requested. Please contact us if you have any questions."
          : "We regret to inform you that one or more of your flights has been cancelled by the airline. Please contact us immediately so we can assist you with alternatives or a refund.";

    const fmtBag = (b) => [
      b.checked_quantity > 0  ? `${b.checked_quantity} x ${b.checked_weight} Checked Baggage` : "No checked baggage",
      b.hand_quantity > 0     ? `${b.hand_quantity} x ${b.hand_weight} Hand Baggage`           : "",
      b.personal_quantity > 0 ? `${b.personal_quantity} x ${b.personal_size} Personal Item`   : "",
    ].filter(Boolean).join("<br>");

    const paxRows = passengers.map((p, pi) => {
      // Baggage: outbound/return split or general
      let bagLines;
      if (p.baggage_mode === "split" && p.baggage_outbound) {
        bagLines = `<b style="font-size:11px;color:#6b7280;">Outbound:</b> ${fmtBag(p.baggage_outbound)}`;
        if (p.baggage_return) bagLines += `<br><b style="font-size:11px;color:#6b7280;">Return:</b> ${fmtBag(p.baggage_return)}`;
      } else {
        bagLines = fmtBag(p);
      }
      const bg = pi % 2 === 0 ? "#f8fafc" : "#ffffff";
      // Ticket numbers: stacked, one per line when two exist
      const tktCell = p.ticket_number_2
        ? `<div style="color:#1e3a5f;font-family:monospace;font-size:13px;font-weight:600;">${p.ticket_number || "—"}</div><div style="color:#1e3a5f;font-family:monospace;font-size:13px;font-weight:600;">${p.ticket_number_2}</div>`
        : `<div style="color:#1e3a5f;font-family:monospace;font-size:13px;font-weight:600;">${p.ticket_number || "—"}</div>`;
      return `<tr style="background:${bg};border-top:1px solid #e2e8f0;">
        <td style="padding:10px 16px;color:#1f2937;font-weight:700;border-right:1px solid #e2e8f0;vertical-align:top;">${(p.last_name||"").toUpperCase()} / ${(p.first_name||"").toUpperCase()} ${p.title}</td>
        <td style="padding:8px 12px;color:#374151;font-size:12px;line-height:1.7;border-right:1px solid #e2e8f0;vertical-align:top;">${bagLines}</td>
        <td style="padding:10px 16px;vertical-align:top;">${tktCell}</td>
      </tr>`;
    }).join("");

    return `<!DOCTYPE html><html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:8px;background:#f3f4f6;font-family:Helvetica,Arial,sans-serif;">
<table style="max-width:640px;margin:0 auto;background:white;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(109,40,217,0.15);" cellpadding="0" cellspacing="0" width="100%">

  <tr><td style="background:#ffffff;padding:20px 40px 16px;text-align:center;border-bottom:3px solid #1e3a5f;">
    <img src="https://thebetterfare.com/images/logo.png" alt="The Better Fare" style="height:48px;width:auto;display:block;margin:0 auto 12px;">
    <div style="display:inline-block;background:#f0f4ff;border:1px solid #bfcfff;border-radius:20px;padding:3px 14px;margin-bottom:8px;">
      <span style="font-size:10px;font-weight:700;color:#1e3a5f;text-transform:uppercase;letter-spacing:2px;">${NOTIFICATION_LABELS[notifType]?.label||"E-Ticket"}</span>
    </div>
    <p style="margin:0;font-size:20px;font-weight:800;color:#1e3a5f;font-family:monospace;letter-spacing:3px;">${pnrDisplay}</p>
    <p style="margin:4px 0 0;font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:1px;">Booking Reference</p>
  </td></tr>

  ${testBannerHtml}
  ${bannerHtml}

  <tr><td style="padding:16px 40px 0;">
    <p style="font-size:16px;color:#1f2937;margin:0 0 6px;font-weight:700;">Dear ${customer.title||""} ${customer.first_name||""} ${customer.last_name||""},</p>
    <p style="font-size:13px;color:#6b7280;margin:0;line-height:1.7;">${greetingBody}</p>
  </td></tr>

  <tr><td style="padding:12px 40px 0;">
    <p style="font-size:10px;font-weight:800;color:#1e3a5f;text-transform:uppercase;letter-spacing:2px;margin:0 0 12px;border-left:3px solid #1e3a5f;padding-left:10px;">Flight Itinerary</p>
    <table style="width:100%;" cellpadding="0" cellspacing="0">${segsHtml}</table>
  </td></tr>

  <tr><td style="padding:10px 40px 0;">
    <table style="width:100%;table-layout:fixed;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;font-size:13px;" cellpadding="0" cellspacing="0">
      <colgroup><col style="width:35%"><col style="width:38%"><col style="width:27%"></colgroup>
      <tr style="background:linear-gradient(90deg,#1e3a5f,#2d5986);">
        <td style="padding:8px 16px;font-size:10px;font-weight:800;color:white;text-transform:uppercase;letter-spacing:2px;">Passenger(s) Name</td>
        <td style="padding:8px 16px;font-size:10px;font-weight:800;color:white;text-transform:uppercase;letter-spacing:2px;">Baggage Limit</td>
        <td style="padding:8px 16px;font-size:10px;font-weight:800;color:white;text-transform:uppercase;letter-spacing:2px;">E-Ticket Number</td>
      </tr>
      ${paxRows}
    </table>
  </td></tr>

  <tr><td style="padding:14px 40px 12px;">
    <p style="font-size:13px;color:#374151;margin:0 0 4px;">For any questions, please reply to this email or contact us on WhatsApp.</p>
    <p style="font-size:11px;color:#9ca3af;margin:0 0 12px;">Please retain this document for your records.</p>
    <div style="background:#faf5ff;border:1px solid #e9d5ff;border-radius:10px;padding:14px 20px;text-align:center;">
      <p style="margin:0 0 6px;font-size:13px;color:#1f2937;font-weight:600;">Enjoyed our service? &#9733;</p>
      <p style="margin:0 0 10px;font-size:12px;color:#6b7280;">Your review means the world to us and helps other travellers find us.</p>
      <a href="https://maps.app.goo.gl/9zfFpfhCeFDPcYac8" style="display:inline-block;background:#6C3084;color:white;padding:8px 24px;border-radius:6px;text-decoration:none;font-size:13px;font-weight:700;">Leave a Google Review</a>
    </div>
  </td></tr>

  <tr><td style="background:linear-gradient(135deg,#1e1b4b,#312e81);padding:20px 40px;text-align:center;">
    <p style="margin:0 0 3px;color:rgba(255,255,255,0.95);font-size:13px;font-weight:700;letter-spacing:0.5px;">The Better Fare &nbsp;&middot;&nbsp; Atypical Agency</p>
    <p style="margin:0;color:rgba(255,255,255,0.4);font-size:11px;">www.thebetterfare.com &nbsp;&middot;&nbsp; KvK 88159620</p>
  </td></tr>

</table>
</body></html>`;
  };

  const handleSave = async () => {
    if (!pnr.trim()) { toast.error("Enter a PNR / booking reference"); return false; }
    setSaving(true);
    try {
      const payload = { segments, passengers, last_notification_type: notifType };
      if (notifType === "ticket") {
        payload.pnr  = pnr;
        payload.pnr2 = pnr2.trim() || null;
      }
      if (ticket) {
        await axios.patch(`${API}/tickets/${ticket.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Ticket updated");
      } else {
        const res = await axios.post(`${API}/tickets`, { booking_id: bookingId, pnr, pnr2: pnr2.trim() || null, document_type: "electronic_ticket", segments, passengers, baggage: null }, { headers: getAuthHeaders() });
        setTicket(res.data);
        toast.success("Ticket created");
      }
      return true;
    } catch (e) {
      console.error("Ticket save error:", e.response?.data || e);
      toast.error(e.response?.data?.detail || "Failed to save ticket");
      return false;
    } finally { setSaving(false); }
  };

  const handleSend = async () => {
    if (notifType === "ticket" && !pnr.trim()) { toast.error("Enter a PNR before sending"); return; }
    if (notifType === "ticket" && passengers.some(p => !p.ticket_number?.trim())) {
      toast.error("Please enter ticket numbers for all passengers before sending");
      return;
    }
    if (!sendEmail.trim()) { toast.error("Enter a recipient email"); return; }
    const recipientName = `${booking?.passengers?.[0]?.first_name||""} ${booking?.passengers?.[0]?.last_name||""}`.trim();
    setSending(true); setShowSendDialog(false);
    try {
      const saved = await handleSave();
      if (!saved) { setSending(false); return; }
      const html = buildHtml(false);
      await axios.post(`${API}/tickets/send-email`, {
        recipient_email: sendEmail.trim(), recipient_name: recipientName, pnr,
        html_content: html, notification_type: notifType,
        ticket_id: ticket?.id || null, segments, is_test: false,
      }, { headers: getAuthHeaders() });
      toast.success(`${NOTIFICATION_LABELS[notifType]?.label||"E-Ticket"} sent to ${sendEmail}!`);
      if (alsoSendInvoice && linkedInvoice?.id) {
        try {
          await axios.post(`${API}/invoices/${linkedInvoice.id}/send-email`,
            { is_test: false, recipient_email: sendEmail.trim() },
            { headers: getAuthHeaders() });
          toast.success("Invoice also sent to customer");
        } catch { toast.error("Invoice send failed — send manually from the booking page"); }
      } else if (!linkedInvoice && notifType === "ticket") {
        toast("No invoice linked to this booking — create one from the booking page", { duration: 6000 });
      }
    } catch (e) {
      console.error("Ticket send error:", e.response?.data || e);
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally { setSending(false); }
  };

  const handleTestSend = async () => {
    if (!pnr.trim()) { toast.error("Enter a PNR before sending a preview"); return; }
    setSendingTest(true);
    try {
      const recipientName = `${booking?.passengers?.[0]?.first_name||""} ${booking?.passengers?.[0]?.last_name||""}`.trim();
      // NOTE: recipient_email is intentionally omitted — backend always sends to admin when is_test=true.
      // Never pass sendEmail here: that field holds the customer address.
      const html = buildHtml(true);
      await axios.post(`${API}/tickets/send-email`, {
        recipient_name: recipientName,
        pnr, html_content: html,
        notification_type: notifType,
        ticket_id: null,   // never mark ticket as sent
        segments,
        is_test: true,
      }, { headers: getAuthHeaders() });
      toast.success("Preview sent to admin inbox — customer status unchanged");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview send failed");
    } finally { setSendingTest(false); }
  };

  const handleDownload = async () => {
    try {
      const html = buildHtml(false);
      const res = await axios.post(`${API}/tickets/generate-pdf`, { recipient_email: "dummy@x.com", recipient_name: "", pnr, html_content: html, notification_type: notifType }, { headers: getAuthHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `E-Ticket-${pnr||bookingId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  const handleCancelTicket = async () => {
    if (!ticket) return;
    setCancelling(true);
    try {
      const res = await axios.post(
        `${API}/tickets/${ticket.id}/cancel`,
        { cancellation_reason: cancelReason },
        { headers: getAuthHeaders() },
      );
      toast.success("Ticket cancelled. Booking status: " + res.data.booking_status);
      setCancelConfirmOpen(false);
      setCancelReason("");
      // Reload to reflect new status — restore by ID, not index, to survive array reordering
      const tRes = await axios.get(`${API}/tickets?booking_id=${bookingId}`, { headers: getAuthHeaders() });
      setTickets(tRes.data);
      const currentId = ticket.id;
      const restoredTicket = tRes.data.find(t => t.id === currentId) || tRes.data[0] || null;
      setTicket(restoredTicket);
      if (restoredTicket) {
        const newIdx = tRes.data.findIndex(t => t.id === restoredTicket.id);
        setSelectedTicketIdx(newIdx >= 0 ? newIdx : 0);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateTicket = async () => {
    if (!ticket) return;
    try {
      await axios.post(`${API}/tickets/${ticket.id}/reactivate`, {}, { headers: getAuthHeaders() });
      toast.success("Ticket reactivated");
      const tRes = await axios.get(`${API}/tickets?booking_id=${bookingId}`, { headers: getAuthHeaders() });
      setTickets(tRes.data);
      const currentId = ticket.id;
      const restoredTicket = tRes.data.find(t => t.id === currentId) || tRes.data[0] || null;
      setTicket(restoredTicket);
      if (restoredTicket) {
        const newIdx = tRes.data.findIndex(t => t.id === restoredTicket.id);
        setSelectedTicketIdx(newIdx >= 0 ? newIdx : 0);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reactivate failed");
    }
  };

  const handleSelectTicket = (idx) => {
    setSelectedTicketIdx(idx);
    const t = tickets[idx];
    if (!t) return;
    setTicket(t);
    setPnr(t.pnr || "");
    setPnr2(t.pnr2 || "");
    setSegments((t.segments || [emptySegment()]).map(s => ({ ...emptySegment(), ...s, status: s.status || "normal" })));
    setPassengers((t.passengers || []).map(p => normPax(p, t.baggage || {})));
    setNotifType(t.last_notification_type || "ticket");
  };

  if (loading) return <DashboardLayout><div className="p-8 flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" /></div></DashboardLayout>;

  const isExisting = !!ticket;
  const customer   = booking?.passengers?.[0];
  const isTicketCancelled = (ticket?.status || "active") === "cancelled";

  const BagRow = ({ pax, paxIdx, bagKey, label }) => {
    const b = pax[bagKey] || emptyBagSection();
    const upd = (f, v) => updateBagField(paxIdx, bagKey, f, v);
    return (
      <div>
        {label && <p className="text-xs font-semibold text-slate-500 mb-1">{label}</p>}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div><Label className="text-xs text-slate-400">Hand Qty</Label><Input type="number" min="0" value={b.hand_quantity} onChange={e => upd("hand_quantity", parseInt(e.target.value)||0)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs text-slate-400">Hand Weight</Label><Input value={b.hand_weight} onChange={e => upd("hand_weight", e.target.value)} placeholder="7kg" className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs text-slate-400">Checked Qty</Label><Input type="number" min="0" value={b.checked_quantity} onChange={e => upd("checked_quantity", parseInt(e.target.value)||0)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs text-slate-400">Checked Weight</Label><Input value={b.checked_weight} onChange={e => upd("checked_weight", e.target.value)} placeholder="23kg" className="mt-1 h-8 text-sm" /></div>
          <div>
            <Label className="text-xs text-slate-400">Personal Item</Label>
            <div className="flex gap-1 mt-1">
              <Input type="number" min="0" value={b.personal_quantity} onChange={e => upd("personal_quantity", parseInt(e.target.value)||0)} className="h-8 text-sm w-14 shrink-0" />
              {b.personal_quantity > 0 && <Input value={b.personal_size} onChange={e => upd("personal_size", e.target.value)} placeholder="40x30x15cm" className="h-8 text-xs flex-1" />}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const GeneralBagRow = ({ pax, paxIdx }) => {
    const upd = (f, v) => updatePax(paxIdx, f, v);
    return (
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div><Label className="text-xs text-slate-400">Hand Qty</Label><Input type="number" min="0" value={pax.hand_quantity} onChange={e => upd("hand_quantity", parseInt(e.target.value)||0)} className="mt-1 h-8 text-sm" /></div>
        <div><Label className="text-xs text-slate-400">Hand Weight</Label><Input value={pax.hand_weight} onChange={e => upd("hand_weight", e.target.value)} placeholder="7kg" className="mt-1 h-8 text-sm" /></div>
        <div><Label className="text-xs text-slate-400">Checked Qty</Label><Input type="number" min="0" value={pax.checked_quantity} onChange={e => upd("checked_quantity", parseInt(e.target.value)||0)} className="mt-1 h-8 text-sm" /></div>
        <div><Label className="text-xs text-slate-400">Checked Weight</Label><Input value={pax.checked_weight} onChange={e => upd("checked_weight", e.target.value)} placeholder="23kg" className="mt-1 h-8 text-sm" /></div>
        <div>
          <Label className="text-xs text-slate-400">Personal Item</Label>
          <div className="flex gap-1 mt-1">
            <Input type="number" min="0" value={pax.personal_quantity} onChange={e => upd("personal_quantity", parseInt(e.target.value)||0)} className="h-8 text-sm w-14 shrink-0" />
            {pax.personal_quantity > 0 && <Input value={pax.personal_size} onChange={e => upd("personal_size", e.target.value)} placeholder="40x30x15cm" className="h-8 text-xs flex-1" />}
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto">

        {/* Header */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/bookings/${bookingId}`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-serif text-xl sm:text-2xl font-bold text-slate-900">E-Ticket Generator</h1>
              {isExisting && <Badge className="bg-purple-100 text-purple-800 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Ticket on file</Badge>}
            </div>
            <p className="text-sm text-slate-500 mt-0.5 truncate">
              {customer ? `${customer.title||""} ${customer.first_name} ${customer.last_name} · ${customer.email}` : booking?.id}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {/* Preview / download tools */}
            <Button variant="outline" size="sm" onClick={() => setShowPreview(true)}><Eye className="w-4 h-4 mr-1.5" />Preview</Button>
            <Button variant="outline" size="sm" onClick={handleDownload}><Download className="w-4 h-4 mr-1.5" />PDF</Button>
            {/* Admin preview — for cancelled tickets, labelled as audit view only */}
            <Button variant="outline" size="sm" onClick={handleTestSend} disabled={sendingTest}
              title={isTicketCancelled ? "View cancelled ticket record (admin only)" : "Sends a preview to the admin inbox only. Customer will NOT receive this."}
              className="border-amber-400 text-amber-700 hover:bg-amber-50">
              <FlaskConical className="w-4 h-4 mr-1.5" />{sendingTest ? "Sending…" : isTicketCancelled ? "View (Cancelled)" : "Admin Preview"}
            </Button>
            {/* Divider */}
            <span className="border-l border-slate-200 self-stretch mx-1" />
            {/* Save / real customer send — blocked for cancelled tickets */}
            <Button variant="outline" size="sm" onClick={handleSave} disabled={saving || isTicketCancelled}
              title={isTicketCancelled ? "Cannot save a cancelled ticket" : undefined}>
              <Pencil className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : isExisting ? "Save" : "Create"}
            </Button>
            <Button size="sm" onClick={() => setShowSendDialog(true)} disabled={sending || isTicketCancelled} className="bg-purple-600 hover:bg-purple-700 text-white"
              title={isTicketCancelled ? "Cannot send a cancelled ticket to customer" : undefined}>
              <Send className="w-4 h-4 mr-1.5" />{sending ? "Sending…" : "Save & Send to Customer"}
            </Button>
          </div>
        </div>

        {/* Send dialog */}
        <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-purple-600" />
                Send {NOTIFICATION_LABELS[notifType]?.label || "E-Ticket"} to Customer
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs bg-purple-50 border border-purple-200 rounded px-3 py-2 text-purple-800 mt-1">
              This sends the real e-ticket to the customer. To send a preview to your own inbox first, close this and click <strong>Admin Preview</strong>.
            </p>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs text-slate-500">Recipient Email</Label>
                <Input value={sendEmail} onChange={e => setSendEmail(e.target.value)} placeholder="customer@email.com" className="mt-1" autoFocus />
                <p className="text-xs text-slate-400 mt-1">Pre-filled from booking — change if needed</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Email Type</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Badge className={NOTIFICATION_LABELS[notifType]?.color}>{NOTIFICATION_LABELS[notifType]?.label}</Badge>
                  <span className="text-xs text-slate-400">{NOTIFICATION_LABELS[notifType]?.desc}</span>
                </div>
              </div>
              {notifType === "ticket" && passengers.some(p => !p.ticket_number?.trim()) && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  ⚠ Enter ticket numbers for all passengers before sending
                </p>
              )}
              {notifType === "ticket" && (
                <div className="border-t pt-3 space-y-2">
                  {linkedInvoice ? (
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input type="checkbox" className="rounded" checked={alsoSendInvoice} onChange={e => setAlsoSendInvoice(e.target.checked)} />
                      <div>
                        <p className="text-sm font-medium text-slate-700">Also send invoice to customer</p>
                        <p className="text-xs text-slate-400">{linkedInvoice.invoice_number} · €{parseFloat(linkedInvoice.invoice_total ?? 0).toFixed(2)}</p>
                      </div>
                    </label>
                  ) : linkedInvoice === null ? (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      ⚠ No invoice linked — create one from the booking page after sending
                    </p>
                  ) : null}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Button
                  onClick={handleSend}
                  disabled={sending || !sendEmail.trim() || (notifType === "ticket" && passengers.some(p => !p.ticket_number?.trim()))}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white">
                  <Send className="w-3 h-3 mr-2" />{sending ? "Sending…" : `Send to ${sendEmail||"…"}`}
                </Button>
                <Button variant="outline" onClick={() => setShowSendDialog(false)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview modal */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-3xl w-full p-0 overflow-hidden">
            <DialogHeader className="px-4 pt-4 pb-2 flex flex-row items-center justify-between">
              <DialogTitle className="text-sm font-semibold">Ticket Preview</DialogTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleDownload}><Download className="w-3 h-3 mr-1" />Download PDF</Button>
              </div>
            </DialogHeader>
            <div className="overflow-auto" style={{ height: "70vh" }}>
              <iframe srcDoc={buildHtml(false)} style={{ width: "100%", height: "900px", border: "none" }} title="ticket-preview-modal" />
            </div>
          </DialogContent>
        </Dialog>

        {/* Existing ticket notice */}
        {isExisting && (
          <div className="mb-5 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start gap-2 text-sm text-purple-800">
            <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-purple-600" />
            <div>
              <strong>Ticket on file</strong> — editing updates the same record.{" "}
              Last sent as: <Badge className={`${NOTIFICATION_LABELS[ticket.last_notification_type]?.color||""} ml-1 text-xs`}>{NOTIFICATION_LABELS[ticket.last_notification_type]?.label||"E-Ticket"}</Badge>
              {ticket.updated_at && <span className="text-purple-500 ml-2 text-xs">· Updated {new Date(ticket.updated_at).toLocaleString()}</span>}
            </div>
          </div>
        )}

        <div className="space-y-5">

          {/* Booking Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <h2 className="font-semibold text-slate-700 text-xs uppercase tracking-widest">Booking Info</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">PNR 1 / Booking Reference *</Label>
                <Input value={pnr} onChange={e => setPnr(e.target.value.toUpperCase())} placeholder="e.g. ZFVVM5" className="mt-1 font-mono" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">PNR 2 (optional)</Label>
                <Input value={pnr2} onChange={e => setPnr2(e.target.value.toUpperCase())} placeholder="e.g. XY5678" className="mt-1 font-mono" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Email Type</Label>
              <Select value={notifType} onValueChange={setNotifType}>
                <SelectTrigger className="mt-1 max-w-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(NOTIFICATION_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      <span className="flex items-center gap-2">
                        <Badge className={`${v.color} text-xs`}>{v.label}</Badge>
                        <span className="text-xs text-slate-500">{v.desc}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Segments */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-semibold text-slate-700 text-xs uppercase tracking-widest">Flight Segments</h2>
              <Button size="sm" variant="outline" onClick={addSeg}><Plus className="w-3 h-3 mr-1" />Add Leg</Button>
            </div>
            {(notifType === "schedule_change" || notifType === "cancellation") && (
              <p className="text-xs text-slate-400 mb-4">
                Mark each flight as <span className="text-amber-600 font-semibold">Changed</span> or <span className="text-red-600 font-semibold">Cancelled</span> — the email highlights them accordingly.
              </p>
            )}
            <div className="space-y-4 mt-3">
              {segments.map((seg, i) => {
                const st = seg.status || "normal";
                const borderCls = st === "changed" ? "border-amber-300 bg-amber-50/40" : st === "cancelled" ? "border-red-300 bg-red-50/40" : "border-slate-200";
                const transit = i > 0 ? computeTransit(segments[i - 1], seg) : null;
                return (
                  <div key={i}>
                  {transit && transit.isStopover && (
                    <div className="flex items-center gap-2 my-1 px-1">
                      <div className="flex-1 border-t border-dashed border-amber-300" />
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-0.5 whitespace-nowrap">
                        &#9679; Stopover in {getAirportName(segments[i-1].arrival_city) || segments[i-1].arrival_city} ({segments[i-1].arrival_city}) &mdash; {fmtTransitDuration(transit.minutes)}
                      </span>
                      <div className="flex-1 border-t border-dashed border-amber-300" />
                    </div>
                  )}
                  <div className={`border rounded-lg p-4 ${borderCls}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Flight {i + 1}</span>
                        {["normal","changed","cancelled"].map(s => (
                          <button key={s} onClick={() => updateSeg(i, "status", s)}
                            className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full border transition-all ${
                              seg.status === s
                                ? s === "normal"  ? "bg-slate-200 text-slate-700 border-slate-400"
                                : s === "changed" ? "bg-amber-200 text-amber-900 border-amber-400"
                                :                   "bg-red-200 text-red-900 border-red-400"
                                : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"
                            }`}>
                            {s === "normal" ? "Normal" : s === "changed" ? "Changed" : "Cancelled"}
                          </button>
                        ))}
                      </div>
                      {segments.length > 1 && (
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-red-400 hover:text-red-600" onClick={() => removeSeg(i)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                      <div><Label className="text-xs text-slate-500">From (IATA)</Label><Input value={seg.departure_city} onChange={e => updateSeg(i, "departure_city", e.target.value.toUpperCase())} placeholder="LHE" className="mt-1 font-mono h-9" /></div>
                      <div><Label className="text-xs text-slate-500">To (IATA)</Label><Input value={seg.arrival_city} onChange={e => updateSeg(i, "arrival_city", e.target.value.toUpperCase())} placeholder="AMS" className="mt-1 font-mono h-9" /></div>
                      <div><Label className="text-xs text-slate-500">Flight No.</Label><Input value={seg.flight_number} onChange={e => updateSeg(i, "flight_number", e.target.value.toUpperCase())} placeholder="KL0157" className="mt-1 font-mono h-9" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div><Label className="text-xs text-slate-500">Dep Date</Label><Input type="date" value={seg.departure_date} onChange={e => updateSeg(i, "departure_date", e.target.value)} className="mt-1 h-9" /></div>
                      <div><Label className="text-xs text-slate-500">Dep Time</Label><Input type="time" value={seg.departure_time} onChange={e => updateSeg(i, "departure_time", e.target.value)} className="mt-1 h-9" /></div>
                      <div><Label className="text-xs text-slate-500">Arr Date</Label><Input type="date" value={seg.arrival_date} onChange={e => updateSeg(i, "arrival_date", e.target.value)} className="mt-1 h-9" /></div>
                      <div><Label className="text-xs text-slate-500">Arr Time</Label><Input type="time" value={seg.arrival_time} onChange={e => updateSeg(i, "arrival_time", e.target.value)} className="mt-1 h-9" /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div><Label className="text-xs text-slate-500">Operated By</Label><Input value={seg.operated_by} onChange={e => updateSeg(i, "operated_by", e.target.value)} placeholder="KLM" className="mt-1 h-9" /></div>
                      <div><Label className="text-xs text-slate-500">Class</Label><Input value={seg.ticket_class} onChange={e => updateSeg(i, "ticket_class", e.target.value)} placeholder="Economy" className="mt-1 h-9" /></div>
                    </div>
                  </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Passengers + Baggage */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-700 text-xs uppercase tracking-widest">Passengers &amp; Baggage</h2>
              <Button size="sm" variant="outline" onClick={addPax}><Plus className="w-3 h-3 mr-1" />Add</Button>
            </div>
            <div className="space-y-5">
              {passengers.map((pax, i) => (
                <div key={i} className="p-4 border border-slate-100 rounded-lg space-y-3">

                  {/* Pax header with remove + apply-to-all */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Passenger {i + 1}</span>
                    <div className="flex gap-1">
                      {passengers.length > 1 && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400 hover:text-slate-600"
                          onClick={() => applyBaggageToAll(i)}>
                          <Copy className="w-3 h-3 mr-1" />Copy baggage to all
                        </Button>
                      )}
                      {passengers.length > 1 && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => removePax(i)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Identity */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                    <div>
                      <Label className="text-xs text-slate-500">Title</Label>
                      <Select value={pax.title} onValueChange={v => updatePax(i, "title", v)}>
                        <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{["Mr","Mrs","Ms","Dr","Prof","Inf"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs text-slate-500">First Name</Label><Input value={pax.first_name} onChange={e => updatePax(i, "first_name", e.target.value)} className="mt-1 h-9" /></div>
                    <div><Label className="text-xs text-slate-500">Last Name</Label><Input value={pax.last_name} onChange={e => updatePax(i, "last_name", e.target.value)} className="mt-1 h-9" /></div>
                    <div>
                      <Label className="text-xs text-slate-500">Gender</Label>
                      <Select value={pax.gender || ""} onValueChange={v => updatePax(i, "gender", v)}>
                        <SelectTrigger className="mt-1 h-9"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="m">Male</SelectItem>
                          <SelectItem value="f">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Ticket numbers */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-slate-500">Ticket # (PNR 1)</Label>
                      <Input value={pax.ticket_number} onChange={e => updatePax(i, "ticket_number", e.target.value)} placeholder="074-1234567890" className="mt-1 h-9 font-mono text-xs" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Ticket # (PNR 2, optional)</Label>
                      <Input value={pax.ticket_number_2 || ""} onChange={e => updatePax(i, "ticket_number_2", e.target.value)} placeholder="leave blank if only one PNR" className="mt-1 h-9 font-mono text-xs" />
                    </div>
                  </div>

                  {/* Baggage */}
                  <div className="pt-2 border-t border-slate-100 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Baggage</span>
                      <div className="flex gap-1">
                        {["general","split"].map(mode => (
                          <button key={mode} onClick={() => {
                            updatePax(i, "baggage_mode", mode);
                            if (mode === "split" && !pax.baggage_outbound) {
                              updateBagField(i, "baggage_outbound", "hand_quantity",    pax.hand_quantity);
                              updateBagField(i, "baggage_outbound", "hand_weight",      pax.hand_weight);
                              updateBagField(i, "baggage_outbound", "checked_quantity", pax.checked_quantity);
                              updateBagField(i, "baggage_outbound", "checked_weight",   pax.checked_weight);
                              updateBagField(i, "baggage_outbound", "personal_quantity",pax.personal_quantity);
                              updateBagField(i, "baggage_outbound", "personal_size",    pax.personal_size);
                            }
                          }}
                            className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full border transition-all ${
                              (pax.baggage_mode || "general") === mode
                                ? "bg-slate-200 text-slate-700 border-slate-400"
                                : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"
                            }`}>
                            {mode === "general" ? "General" : "Outbound / Return"}
                          </button>
                        ))}
                      </div>
                    </div>

                    {(pax.baggage_mode || "general") === "general" ? (
                      <GeneralBagRow pax={pax} paxIdx={i} />
                    ) : (
                      <div className="space-y-3">
                        <BagRow pax={pax} paxIdx={i} bagKey="baggage_outbound" label="Outbound" />
                        <BagRow pax={pax} paxIdx={i} bagKey="baggage_return"   label="Return (optional — leave as default if same as outbound)" />
                      </div>
                    )}
                  </div>

                </div>
              ))}
            </div>
          </div>

        </div>

        {ticket?.id && (
          <div className="max-w-4xl mx-auto px-4 pb-8">
            <CommunicationHistory
              sourceType="ticket"
              sourceId={ticket.id}
              bookingId={bookingId}
            />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
